import { useCallback, useEffect, useState } from 'react'
import { emporionToast as toast } from '@admin/toast'
import type { CmsPage, Section } from '../../types'
import { useI18n } from '../../i18n'
import { requestAuthedJson, resolveTokenFromAuthHeaders } from '../../lib/api/http'

type Args = {
  token: string | null
  authHeaders: (withJson?: boolean) => Record<string, string>
  setSection: (section: Section) => void
}

const usePagesManager = ({ token, authHeaders, setSection }: Args) => {
  const { t } = useI18n()
  const [pages, setPages] = useState<CmsPage[]>([])
  const [pageEditorMode, setPageEditorMode] = useState<'list' | 'create' | 'edit'>('list')
  const [pageEditorId, setPageEditorId] = useState<string | undefined>(undefined)

  const loadPages = useCallback(async () => {
    if (!token) return
    try {
      const data = await requestAuthedJson({
        token: resolveTokenFromAuthHeaders(authHeaders),
        path: '/admin/pages?limit=200',
        fallbackError: t('pages.toast.loadError'),
      })
      const parsed: CmsPage[] = (Array.isArray(data?.pages) ? data.pages : []).map(
        (page: Record<string, unknown>) => ({
          id: (page._id as string) ?? (page.id as string),
          title: String(page.title ?? ''),
          slug: String(page.slug ?? ''),
          locale: page.locale === 'en-US' ? 'en-US' : 'it-IT',
          translationGroupId:
            typeof page.translationGroupId === 'string' ? page.translationGroupId : undefined,
          translationSourcePageId:
            typeof page.translationSourcePageId === 'string'
              ? page.translationSourcePageId
              : null,
          translationStatus:
            page.translationStatus === 'in_progress'
              ? 'in_progress'
              : page.translationStatus === 'published'
              ? 'published'
              : 'draft',
          isSystem: Boolean(page.isSystem),
          systemKey:
            page.systemKey === 'homepage' || page.systemKey === 'privacy-policy' || page.systemKey === 'cookie-policy'
              ? page.systemKey
              : undefined,
          status: (page.status as CmsPage['status']) ?? 'draft',
          excerpt: (page.excerpt as string | null | undefined) ?? null,
          metaTitle: (page.metaTitle as string | null | undefined) ?? null,
          metaDescription: (page.metaDescription as string | null | undefined) ?? null,
          createdAt: page.createdAt as string | undefined,
          updatedAt: page.updatedAt as string | undefined,
          publishedAt: (page.publishedAt as string | null | undefined) ?? null,
          deletedAt: (page.deletedAt as string | null | undefined) ?? null,
          deletedBy: (page.deletedBy as string | null | undefined) ?? null,
          restoredAt: (page.restoredAt as string | null | undefined) ?? null,
          restoredBy: (page.restoredBy as string | null | undefined) ?? null,
          blocksCount: typeof page.blocksCount === 'number' ? page.blocksCount : undefined,
          content: Array.isArray(page.content) ? page.content : undefined,
        }),
      )
      setPages(parsed)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('pages.toast.loadError'))
    }
  }, [authHeaders, t, token])

  useEffect(() => {
    if (pageEditorMode !== 'edit' || !pageEditorId) return
    const page = pages.find((entry) => entry.id === pageEditorId)
    const isLegalPage =
      page?.systemKey === 'privacy-policy' ||
      page?.systemKey === 'cookie-policy' ||
      page?.slug === 'privacy-policy' ||
      page?.slug === 'cookie-policy'
    if (!isLegalPage) return
    setPageEditorMode('list')
    setPageEditorId(undefined)
    setSection('site-legal')
  }, [pageEditorId, pageEditorMode, pages, setPageEditorId, setPageEditorMode, setSection])

  const openCreatePage = useCallback(() => {
    setPageEditorMode('create')
    setPageEditorId(undefined)
    setSection('pages')
  }, [setSection])

  const openEditPage = useCallback(
    (id: string) => {
      const page = pages.find((entry) => entry.id === id)
      if (
        page?.systemKey === 'privacy-policy' ||
        page?.systemKey === 'cookie-policy' ||
        page?.slug === 'privacy-policy' ||
        page?.slug === 'cookie-policy'
      ) {
        setPageEditorMode('list')
        setPageEditorId(undefined)
        setSection('site-legal')
        return
      }
      setPageEditorMode('edit')
      setPageEditorId(id)
      setSection('pages')
    },
    [pages, setSection],
  )

  const handlePageSaved = useCallback(
    (id: string) => {
      setPageEditorMode('edit')
      setPageEditorId(id)
      void loadPages()
      toast.success(t('pages.toast.saved'))
    },
    [loadPages, t],
  )

  const handlePageCancel = useCallback(() => {
    setPageEditorMode('list')
    setPageEditorId(undefined)
    void loadPages()
  }, [loadPages])

  return {
    pages,
    setPages,
    pageEditorMode,
    pageEditorId,
    setPageEditorMode,
    setPageEditorId,
    loadPages,
    openCreatePage,
    openEditPage,
    handlePageSaved,
    handlePageCancel,
  }
}

export default usePagesManager
